import React, { useContext, useEffect, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import {
  AdminRestriction,
  AdminRestrictionQuota,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import {
  RestrictionName,
  RestrictionQuota,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import SubscriptionAction from "./subscription-action";
import {
  getOrgRestrictions,
  updateOrgRestriction,
} from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { OrgDataContext } from "context/organisation-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import {
  UpdateSeatMovementsRestrictionWrapperProps,
  UrlParamTypes,
} from "types/org-display";

const convertRestrictionName = (name: RestrictionName) =>
  `RESTRICTION_NAME_${RestrictionName[name]}`;

const fullSeatLimitRestrictionName = convertRestrictionName(
  RestrictionName.SUBSCRIPTIONS_FULL_SEAT_LIMIT,
);

const liteSeatLimitRestrictionName = convertRestrictionName(
  RestrictionName.SUBSCRIPTIONS_LITE_SEAT_LIMIT,
);

// Maps each selectable seat type to the RestrictionName enum used when writing.
const restrictionNameEnumByName: Record<string, RestrictionName> = {
  [fullSeatLimitRestrictionName]: RestrictionName.SUBSCRIPTIONS_FULL_SEAT_LIMIT,
  [liteSeatLimitRestrictionName]: RestrictionName.SUBSCRIPTIONS_LITE_SEAT_LIMIT,
};

const getQuotaLimit = (
  restriction: AdminRestriction | undefined,
): number | undefined => {
  if (restriction?.restriction.case === "quota") {
    const quotaRestriction = restriction.restriction
      .value as AdminRestrictionQuota;
    return quotaRestriction.userRestriction?.limit;
  }
  return undefined;
};

const SetSeatLimitRestriction = ({
  subscription,
}: UpdateSeatMovementsRestrictionWrapperProps) => {
  const theme = useTheme();
  const { regionCode } = useParams<UrlParamTypes>();
  const [fullSeatLimit, setFullSeatLimit] = useState<number | undefined>(
    undefined,
  );
  const [liteSeatLimit, setLiteSeatLimit] = useState<number | undefined>(
    undefined,
  );
  const [restrictionLoading, setRestrictionLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRestrictionName, setSelectedRestrictionName] = useState(
    fullSeatLimitRestrictionName,
  );
  const [limitInput, setLimitInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const { orgData } = useContext(OrgDataContext);
  const { showError } = useContext(SnackbarContext);

  const currentLimitFor = (restrictionName: string) =>
    restrictionName === fullSeatLimitRestrictionName
      ? fullSeatLimit
      : liteSeatLimit;

  const getSeatLimitRestriction = async () => {
    setRestrictionLoading(true);
    try {
      const restrictionsResponse = await getOrgRestrictions(
        {
          organizationId: toUUID(orgData.id),
          restrictionNames: [
            fullSeatLimitRestrictionName,
            liteSeatLimitRestrictionName,
          ],
        },
        callOptionsHeaders(regionCode, orgData.id),
      );

      setFullSeatLimit(
        getQuotaLimit(
          restrictionsResponse.restrictions[fullSeatLimitRestrictionName],
        ),
      );
      setLiteSeatLimit(
        getQuotaLimit(
          restrictionsResponse.restrictions[liteSeatLimitRestrictionName],
        ),
      );
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setRestrictionLoading(false);
    }
  };

  useEffect(() => {
    if (orgData?.id) {
      getSeatLimitRestriction();
    }
  }, [orgData?.id, regionCode]);

  const handleOpenDialog = () => {
    setSelectedRestrictionName(fullSeatLimitRestrictionName);
    const current = currentLimitFor(fullSeatLimitRestrictionName);
    setLimitInput(current !== undefined ? String(current) : "");
    setOpenDialog(true);
  };

  // Auto-sync the number field to the newly selected seat type's stored limit.
  const handleSeatTypeChange = (event: SelectChangeEvent) => {
    const restrictionName = event.target.value;
    setSelectedRestrictionName(restrictionName);
    const current = currentLimitFor(restrictionName);
    setLimitInput(current !== undefined ? String(current) : "");
  };

  const parsedLimit = Number(limitInput);
  const isApplyDisabled =
    isSaving ||
    limitInput.trim() === "" ||
    Number.isNaN(parsedLimit) ||
    parsedLimit < 0;

  const buildRestriction = (limit: number) =>
    new AdminRestriction({
      name: restrictionNameEnumByName[selectedRestrictionName],
      restriction: {
        case: "quota",
        value: new AdminRestrictionQuota({
          userRestriction: new RestrictionQuota({
            limit,
          }),
        }),
      },
    });

  const handleApply = async () => {
    if (isApplyDisabled) {
      return;
    }
    setIsSaving(true);
    try {
      await updateOrgRestriction(
        {
          organizationId: toUUID(orgData.id),
          restrictionName: selectedRestrictionName,
          restriction: buildRestriction(parsedLimit),
        },
        callOptionsHeaders(regionCode, orgData.id),
      );
      setOpenDialog(false);
      await getSeatLimitRestriction();
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <SubscriptionAction
        title="Seat Limit:"
        // Wait for subscription and restriction data to be loaded before displaying
        middleElem={
          subscription && !restrictionLoading ? (
            <Typography
              sx={{
                mt: 1,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: theme.typography.body2.fontWeight,
              }}
            >
              {`${fullSeatLimit ?? "None"} Full Seats, ${liteSeatLimit ?? "None"} Lite Seats`}
              <Tooltip
                title="This limit applies to in-product actions such as adding and removing users."
                placement="right"
              >
                <IconButton
                  size="small"
                  sx={{ padding: "2px", marginLeft: "4px" }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
          ) : undefined
        }
      >
        {restrictionLoading ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            data-testid="btn-set-seat-limit"
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleOpenDialog}
          >
            UPDATE SEAT LIMIT
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Update Seat Limit"
        cancelBtnLabel="CANCEL"
        confirmBtnLabel="APPLY"
        open={openDialog}
        setOpen={setOpenDialog}
        disabled={isApplyDisabled}
        disableCancelBtn={isSaving}
        disableCloseOnConfirm
        onConfirm={handleApply}
        onCancel={() => {
          setOpenDialog(false);
        }}
      >
        <Grid
          container
          direction="column"
          spacing={2}
          sx={{ mt: 0 }}
          data-testid="set-seat-limit-form"
        >
          <Grid item>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="seat-type-label">Seat Type</InputLabel>
              <Select
                labelId="seat-type-label"
                label="Seat Type"
                value={selectedRestrictionName}
                data-testid="select-seat-type"
                onChange={handleSeatTypeChange}
              >
                <MenuItem value={fullSeatLimitRestrictionName}>
                  Full Seat
                </MenuItem>
                <MenuItem value={liteSeatLimitRestrictionName}>
                  Lite Seat
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item>
            <TextField
              fullWidth
              size="small"
              type="number"
              variant="outlined"
              label="Seat Limit"
              value={limitInput}
              onChange={(e) => setLimitInput(e.target.value)}
              InputProps={{
                inputProps: {
                  min: 0,
                  "data-testid": "input-seat-limit",
                },
              }}
            />
          </Grid>
        </Grid>
      </ConfirmDialog>
    </>
  );
};

export default SetSeatLimitRestriction;
